import { lazy, Suspense, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { LazyMotion, domAnimation } from "framer-motion";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Index from "./pages/Index";
import { initAnalytics, trackPageView } from "@/lib/analytics";
import { captureUTM } from "@/lib/utm";
import Threshold from "@/components/effects/Threshold";
import CursorGlow from "@/components/effects/CursorGlow";

const Roadmap = lazy(() => import("./pages/Roadmap"));
const Apply = lazy(() => import("./pages/Apply"));
const Book = lazy(() => import("./pages/Book"));
const Confirmation = lazy(() => import("./pages/Confirmation"));
const Thanks = lazy(() => import("./pages/Thanks"));
const Privacy = lazy(() => import("./pages/legal/Privacy"));
const Terms = lazy(() => import("./pages/legal/Terms"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

function RouteTracker() {
  const loc = useLocation();
  useEffect(() => { trackPageView(loc.pathname + loc.search); }, [loc.pathname, loc.search]);
  return null;
}

function Bootstrap() {
  useEffect(() => {
    captureUTM();
    initAnalytics();
  }, []);
  return null;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LazyMotion features={domAnimation} strict>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Bootstrap />
          <RouteTracker />
          <Threshold />
          <CursorGlow />
          <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/apply" element={<Apply />} />
              <Route path="/roadmap" element={<Roadmap />} />
              <Route path="/book" element={<Book />} />
              <Route path="/confirmation" element={<Confirmation />} />
              <Route path="/thanks" element={<Thanks />} />
              <Route path="/legal/privacy" element={<Privacy />} />
              <Route path="/legal/terms" element={<Terms />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </LazyMotion>
  </QueryClientProvider>
);

export default App;
